import { Dispatch, Fragment, SetStateAction } from 'react';
import { KeyedMutator } from 'swr';
import { useTranslation } from 'react-i18next';
import PageContent from 'components/PageContent';
import { IFlightTicketProposal } from 'entities/Flights/sdk.ts';
import AttendeeBox from 'pages/FlightOffer/AttendeesSection/AttendeeBox';
import { ArrowDownTrayIcon } from '@heroicons/react/16/solid';
import airplane from 'assets/airplane.svg';
import { FlightTicketProposalStatusEnum } from 'utils/constants.ts';
import TotalSection from 'pages/FlightOffer/TotalSection';
import { FlightOfferViewEnum } from 'pages/FlightOffer/constants.tsx';
import { Button } from 'components/ui/button.tsx';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from 'components/ui/accordion.tsx';
import { H2 } from 'components/ui/typography.tsx';

interface IConfirmedFlightOfferPageProps {
  flightTicketProposal: IFlightTicketProposal;
  currency: string | null;
  setCurrency: Dispatch<SetStateAction<string | null>>;
  flightTicketProposalMutate: KeyedMutator<IFlightTicketProposal>;
}

const ConfirmedFlightOfferPage = ({
  flightTicketProposal,
  currency,
  setCurrency,
  flightTicketProposalMutate,
}: IConfirmedFlightOfferPageProps) => {
  const { t } = useTranslation();
  const hasFlightTickets =
    [
      FlightTicketProposalStatusEnum.TICKETS_SENT,
      FlightTicketProposalStatusEnum.OPERATED,
      FlightOfferViewEnum.COMPLETED,
    ].includes(flightTicketProposal?.status) && flightTicketProposal.flight_tickets_pdf !== null;

  return (
    <Fragment>
      <PageContent>
        <div className='flex flex-col gap-[80px] max-md:gap-[60px]'>
          <div className='flex flex-col max-lg:gap-[16px] '>
            <div className='flex justify-between max-lg:flex-col max-lg:items-center max-lg:gap-[16px]'>
              <H2>{t('flightOffer.attendees')}</H2>
            </div>
            <Accordion>
              <AccordionItem>
                <AccordionTrigger className='flex gap-[6px] justify-center items-center'>
                  <div className='w-[24px] h-[24px] rounded-[50%] bg-primary text-white flex items-center justify-center'>
                    {flightTicketProposal.travel_request_people.length}
                  </div>
                  <div className='font-[MuseoSansCyrl] text-grey700 text-base'>{t('flightOffer.showAllAttendees')}</div>
                </AccordionTrigger>
                <AccordionContent className='p-4 flex gap-[24px] flex-wrap shadow-md rounded-xl'>
                  {flightTicketProposal?.travel_request_people?.map((person) => <AttendeeBox person={person} />)}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
          <div className='flex flex-col max-lg:gap-[16px]'>
            <div className='flex justify-between  max-lg:flex-col max-lg:items-center max-lg:gap-[16px]'>
              <H2>{t('flightOffer.flightInformation')}</H2>
            </div>
            <div className='px-[10px] py-[15px] rounded-[12px] shadow-[4px_4px_4px_4px_rgba(0,0,0,0.04)] flex justify-between'>
              <div className='flex items-center gap-[6px]'>
                <img src={airplane} alt='airplane' />
                <div className='font-[MuseoSansCyrl] text-primary text-[30px] max-lg:text-[16px]'>
                  {t('confirmedFlightOffer.flightTickets')}
                </div>
              </div>
              {!hasFlightTickets ? (
                <div className='flex items-center'>
                  <div className='font-[MuseoSansCyrl] text-primary text-[16px] max-lg:text-[12px]'>
                    {t('confirmedFlightOffer.ticketArrival')}
                  </div>
                </div>
              ) : (
                <a
                  href={flightTicketProposal.flight_tickets_pdf?.url}
                  target='_blank'
                  rel='noopener noreferrer'
                  download
                >
                  <Button className='w-[320px] flex gap-[8px] items-center justify-center max-lg:w-fit max-sm:hidden'>
                    {t('flightOffer.download')}
                    <ArrowDownTrayIcon className='size-4 fill-white' />
                  </Button>
                  <ArrowDownTrayIcon className='size-6 fill-primary sm:hidden' />
                </a>
              )}
            </div>
          </div>
        </div>
      </PageContent>
      <TotalSection
        view={FlightOfferViewEnum.COMPLETED}
        currency={currency}
        setCurrency={setCurrency}
        isConfirmDisabled={true}
        flightTicketProposal={flightTicketProposal}
        handleConfirmClick={() => {}}
        handleCancelClick={() => {}}
        flightTicketProposalMutate={flightTicketProposalMutate}
      />
    </Fragment>
  );
};

export default ConfirmedFlightOfferPage;
